import React, { useContext } from 'react';
import { CompanyContext } from '../../../../../context/CompanyContext';
import { SuperUserContext } from '../../../../../context/SuperUserContext';
import { SuperUserRole } from '../../../../../types/superUser';
import { Checkbox } from '../../../../components/Checkbox';
import { useMutation } from '@apollo/client';
import { Company } from '../../../../../types/company';
import {
  CompanyMutations,
  UpdateCompanyResponse,
  UpdateCompanyVariables,
} from '../../../../../graphql/company.graphql';
import { translate } from '../../../../../types/translation/Translator';
import { TranslationKey } from '../../../../../types/translation/TranslationKey';
import { Surface } from '../../../../components/Surface';
import { Button } from '../../../../components/Button';
import DuplicateCompanyModal from './DuplicateCompanyModal';

interface CompanySuperUserSettingsPaneProps {}

export default function CompanySuperUserSettingsPane({}: CompanySuperUserSettingsPaneProps) {
  const { currentCompany, setCurrentCompany } = useContext(CompanyContext);
  const { superUser } = useContext(SuperUserContext);

  const [duplicateModalOpen, setDuplicateModalOpen] = React.useState(false);

  if (superUser?.role !== SuperUserRole.administrator) return null;

  const [update, { loading }] = useMutation<UpdateCompanyResponse, UpdateCompanyVariables>(CompanyMutations.update, {
    onCompleted: response => {
      setCurrentCompany(new Company(response.updateCompany));
    },
  });

  const handleUpdate = async (company: Company) => {
    if (loading) return;
    await update({ variables: { company: company.forUpdate() } });
  };

  const company = new Company(currentCompany);

  return (
    <Surface>
      <div className='grid gap-4'>
        <p className='section-title'>Super User Settings</p>

        <div className='grid gap-2'>
          <p className='text-base font-semibold text-title'>{translate({ key: TranslationKey.partnerPortal })}</p>
          {superUser?.role === SuperUserRole.administrator && (
            <Checkbox
              label={translate({ key: TranslationKey.partner })}
              disabled={loading}
              value={company.partner}
              onChange={async v => await handleUpdate(company.withPartner(v))}
            />
          )}
        </div>

        <div className='grid gap-2'>
          <p className='text-base font-semibold text-title'>Duplicate company</p>
          <p className='subtitle'>
            Duplicating the company will create a new company with most data copied from the current one, with the
            exception of users and their company- & stock location roles.
          </p>
          <div>
            <Button text={'Duplicate'} onClick={() => setDuplicateModalOpen(true)} />
          </div>
          {duplicateModalOpen && (
            <DuplicateCompanyModal company={currentCompany} open={duplicateModalOpen} setOpen={setDuplicateModalOpen} />
          )}
        </div>
      </div>
    </Surface>
  );
}
